/* iter327N — Micronutrient Quick-Add Modal
 *
 * Curated list of vitamins, minerals and B-complex supplements that the
 * user can log into the diary with one tap. Each pill carries a typical
 * therapeutic dose + a "logged as supplement, no calories" macro profile
 * so it does NOT distort DIL or calorie tracking.
 *
 * Backend route reused: POST /api/meals/add  — supplement_info dict
 * surfaces dose / form to the diary UI.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Pill, Droplet, Leaf, Sparkles } from "lucide-react";

// Each micronutrient entry maps to typical daily dose + supplement_info
// metadata. Logged as zero-calorie meal entries so they show in diary
// without affecting macros / DIL.
const MICRONUTRIENTS = [
  // ─── Cofactors for Col3 synthesis ───
  { id: "vit_c_500",   group: "col3", name_bg: "Витамин C 500 mg",      name_en: "Vitamin C 500 mg",      dose: "500 mg",   form_bg: "капсула",  Icon: Sparkles, color: "#F97316", note_bg: "Кофактор за колагенов синтез" },
  { id: "vit_c_1000",  group: "col3", name_bg: "Витамин C 1000 mg",     name_en: "Vitamin C 1000 mg",     dose: "1000 mg",  form_bg: "капсула",  Icon: Sparkles, color: "#F97316", note_bg: "Висока доза при стрес/имунитет" },
  { id: "vit_k2_100",  group: "col3", name_bg: "Витамин K2 (MK-7) 100 μg", name_en: "Vitamin K2 (MK-7) 100 μg", dose: "100 μg",  form_bg: "капсула",  Icon: Leaf, color: "#10B981", note_bg: "Калциев metabolism + cross-linking" },
  { id: "iron_18",     group: "col3", name_bg: "Желязо (Fe) 18 mg",     name_en: "Iron (Fe) 18 mg",       dose: "18 mg",    form_bg: "таблетка", Icon: Droplet, color: "#EF4444", note_bg: "С храна + Vit C за абсорбция" },
  { id: "iron_chelate_25", group: "col3", name_bg: "Желязо bisglycinate 25 mg", name_en: "Iron bisglycinate 25 mg", dose: "25 mg",  form_bg: "капсула",  Icon: Droplet, color: "#EF4444", note_bg: "Хелатно — без стомашен стрес" },
  { id: "copper_2",    group: "col3", name_bg: "Мед (Cu) 2 mg",         name_en: "Copper (Cu) 2 mg",      dose: "2 mg",     form_bg: "капсула",  Icon: Pill, color: "#A855F7", note_bg: "Lysyl oxidase кофактор" },
  { id: "zinc_15",     group: "col3", name_bg: "Цинк (Zn) 15 mg",       name_en: "Zinc (Zn) 15 mg",       dose: "15 mg",    form_bg: "капсула",  Icon: Pill, color: "#3B82F6", note_bg: "Имунитет + рана зарастване" },
  { id: "manganese_2", group: "col3", name_bg: "Манган 2 mg",           name_en: "Manganese 2 mg",        dose: "2 mg",     form_bg: "капсула",  Icon: Pill, color: "#8B5CF6", note_bg: "Колагенов кросслинкинг" },

  // ─── B-complex ───
  { id: "b12_1000",    group: "b", name_bg: "Витамин B12 (метилкобаламин) 1000 μg", name_en: "Vitamin B12 1000 μg", dose: "1000 μg", form_bg: "сублингвална", Icon: Pill, color: "#EC4899", note_bg: "Енергия, метилиране" },
  { id: "b9_400",      group: "b", name_bg: "Фолат (B9) 400 μg",     name_en: "Folate (B9) 400 μg",   dose: "400 μg",   form_bg: "капсула",  Icon: Pill, color: "#EC4899", note_bg: "ДНК синтез" },
  { id: "b6_50",       group: "b", name_bg: "Витамин B6 (P5P) 50 mg", name_en: "Vitamin B6 (P5P) 50 mg", dose: "50 mg", form_bg: "капсула",  Icon: Pill, color: "#EC4899", note_bg: "Невротрансмитери" },
  { id: "b1_50",       group: "b", name_bg: "Витамин B1 (тиамин) 50 mg", name_en: "Vitamin B1 (thiamine) 50 mg", dose: "50 mg", form_bg: "капсула", Icon: Pill, color: "#EC4899", note_bg: "Глюкозен метаболизъм" },
  { id: "b2_50",       group: "b", name_bg: "Витамин B2 (рибофлавин) 50 mg", name_en: "Vitamin B2 (riboflavin) 50 mg", dose: "50 mg", form_bg: "капсула", Icon: Pill, color: "#EC4899", note_bg: "Енергиен метаболизъм" },
  { id: "b3_500",      group: "b", name_bg: "Витамин B3 (ниацинамид) 500 mg", name_en: "Vitamin B3 (niacinamide) 500 mg", dose: "500 mg", form_bg: "капсула", Icon: Pill, color: "#EC4899", note_bg: "NAD+ прекурсор" },
  { id: "b_complex",   group: "b", name_bg: "B-комплекс (B1-B12)",   name_en: "B-Complex (B1-B12)",    dose: "1 капс",   form_bg: "капсула",  Icon: Pill, color: "#EC4899", note_bg: "Универсален B стек" },

  // ─── Other essentials ───
  { id: "vit_d3_2000", group: "other", name_bg: "Витамин D3 2000 IU",  name_en: "Vitamin D3 2000 IU",   dose: "2000 IU",  form_bg: "капки",    Icon: Sparkles, color: "#FBBF24", note_bg: "Имунитет + калций" },
  { id: "vit_d3_5000", group: "other", name_bg: "Витамин D3 5000 IU",  name_en: "Vitamin D3 5000 IU",   dose: "5000 IU",  form_bg: "капки",    Icon: Sparkles, color: "#FBBF24", note_bg: "Висока доза при дефицит" },
  { id: "magnesium_400", group: "other", name_bg: "Магнезий (бисглицинат) 400 mg", name_en: "Magnesium bisglycinate 400 mg", dose: "400 mg", form_bg: "капсула", Icon: Leaf, color: "#10B981", note_bg: "Сън, мускули, инсулин" },
  { id: "selenium_200", group: "other", name_bg: "Селен 200 μg",       name_en: "Selenium 200 μg",      dose: "200 μg",   form_bg: "капсула",  Icon: Pill, color: "#06B6D4", note_bg: "Тиреоидна функция" },
  { id: "iodine_150",  group: "other", name_bg: "Йод 150 μg",          name_en: "Iodine 150 μg",        dose: "150 μg",   form_bg: "капки",    Icon: Droplet, color: "#06B6D4", note_bg: "Тиреоидни хормони" },
  { id: "omega_3_1000", group: "other", name_bg: "Омега-3 EPA+DHA 1000 mg", name_en: "Omega-3 EPA+DHA 1000 mg", dose: "1000 mg", form_bg: "капсула", Icon: Droplet, color: "#0EA5E9", note_bg: "Анти-възпалителен" },
  { id: "coq10_100",   group: "other", name_bg: "CoQ10 (убихинол) 100 mg", name_en: "CoQ10 (ubiquinol) 100 mg", dose: "100 mg", form_bg: "капсула", Icon: Sparkles, color: "#FBBF24", note_bg: "Митохондриална енергия" },
];

const GROUP_LABELS = {
  col3: { bg: "🧬 Col3 / Колагенов стек", en: "🧬 Col3 / Collagen stack" },
  b:    { bg: "💊 B-витамини",            en: "💊 B-vitamins" },
  other:{ bg: "✨ Други есенциални",       en: "✨ Other essentials" },
};

export default function MicronutrientQuickAddModal({ open, onOpenChange, onLogged }) {
  const { API } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const [loadingId, setLoadingId] = useState(null);

  const handleLog = async (micro) => {
    setLoadingId(micro.id);
    try {
      // Default to current hour for circadian bucket alignment
      const now = new Date();
      const hh = now.getHours().toString().padStart(2, "0");
      const mm = now.getMinutes().toString().padStart(2, "0");
      await axios.post(`${API}/meals/add`, {
        meal_type: hh < "11" ? "breakfast" : hh < "15" ? "lunch" : hh < "19" ? "snack" : "dinner",
        food_name: bg ? micro.name_bg : micro.name_en,
        amount_g: 1,
        calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, sugar: 0, saturated_fat: 0,
        log_time: `${hh}:${mm}`,
        supplement_info: {
          dosage: micro.dose,
          form: micro.form_bg,
          micronutrient_id: micro.id,
          category: "micronutrient",
        },
      });
      toast.success(bg ? `${micro.name_bg} добавена!` : `${micro.name_en} logged!`);
      window.dispatchEvent(new Event("meal-log-changed"));
      onLogged?.();
    } catch (e) {
      toast.error(bg ? "Грешка при записване" : "Logging error");
    } finally {
      setLoadingId(null);
    }
  };

  const groups = [
    { key: "col3",  items: MICRONUTRIENTS.filter((m) => m.group === "col3") },
    { key: "b",     items: MICRONUTRIENTS.filter((m) => m.group === "b") },
    { key: "other", items: MICRONUTRIENTS.filter((m) => m.group === "other") },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`max-w-2xl max-h-[85vh] overflow-y-auto ${
          isDark ? "bg-[#0a0420] border-fuchsia-900/40" : "bg-white"
        }`}
        data-testid="micronutrient-quickadd-modal"
      >
        <DialogHeader>
          <DialogTitle className={`text-base ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "🧬 Витамини & минерали — бързо добавяне" : "🧬 Vitamins & minerals — quick log"}
          </DialogTitle>
          <p className={`text-[11px] mt-1 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
            {bg
              ? "Едно натискане записва добавката с типичната доза. Не повлиява инсулиновия товар или калории."
              : "One click logs the supplement with its typical dose. Does not affect Insulin Load or calories."}
          </p>
        </DialogHeader>

        {groups.map((g) => (
          <div key={g.key} className="mt-4">
            <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${isDark ? "text-fuchsia-300" : "text-fuchsia-700"}`}>
              {bg ? GROUP_LABELS[g.key].bg : GROUP_LABELS[g.key].en}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {g.items.map((micro) => {
                const Icon = micro.Icon;
                const loading = loadingId === micro.id;
                return (
                  <Button
                    key={micro.id}
                    type="button"
                    onClick={() => handleLog(micro)}
                    disabled={loading || loadingId !== null}
                    data-testid={`micro-add-${micro.id}`}
                    variant="outline"
                    className={`h-auto py-3 px-3 flex items-start gap-3 text-left rounded-xl transition-all ${
                      isDark
                        ? "bg-[hsl(223,47%,11%)] border-fuchsia-900/40 hover:bg-[hsl(223,47%,15%)] hover:border-fuchsia-500"
                        : "bg-white border-gray-200 hover:bg-fuchsia-50 hover:border-fuchsia-300"
                    }`}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${micro.color}20`, color: micro.color }}
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                        {bg ? micro.name_bg : micro.name_en}
                      </div>
                      <div className={`text-[10px] mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                        {micro.dose} · {micro.form_bg}
                      </div>
                      <div className={`text-[9px] italic mt-0.5 ${isDark ? "text-gray-500" : "text-gray-500"}`}>
                        {micro.note_bg}
                      </div>
                    </div>
                  </Button>
                );
              })}
            </div>
          </div>
        ))}

        <div className={`text-[10px] mt-4 italic ${isDark ? "text-gray-500" : "text-gray-500"}`}>
          {bg
            ? "💡 Винаги се консултирайте с лекар преди да започнете нов supplement, особено при бременност, бъбречни/чернодробни проблеми или прием на лекарства."
            : "💡 Always consult a doctor before starting a new supplement, especially during pregnancy, kidney/liver issues or while on medications."}
        </div>
      </DialogContent>
    </Dialog>
  );
}
